/** Scheda prodotto: galleria, selettore varianti, quantità, barra sticky, accessori. */
(() => {
  'use strict';

  /* ---- galleria ---- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.slides = [...this.querySelectorAll('.gallery__slide')];
      this.thumbs = [...this.querySelectorAll('[data-gallery-to]')];
      this.counter = this.querySelector('[data-gallery-counter]');
      this.prev = this.querySelector('[data-gallery-prev]');
      this.next = this.querySelector('[data-gallery-next]');
      this.index = 0;
      this.prev?.addEventListener('click', () => this.go(this.index - 1));
      this.next?.addEventListener('click', () => this.go(this.index + 1));
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(parseInt(t.dataset.galleryTo, 10))));
      this.querySelectorAll('[data-gallery-filter]').forEach((b) =>
        b.addEventListener('click', () => {
          const type = b.dataset.galleryFilter;
          const target = type === 'video' ? this.slides.find((s) => /video/.test(s.dataset.mediaType)) : this.slides[0];
          if (target) this.go(parseInt(target.dataset.index, 10));
        })
      );
      this.track?.addEventListener('scroll', window.themeDebounce(() => this.sync(), 80), { passive: true });
      document.addEventListener('theme:variant:change', (e) => {
        if (e.detail?.page && e.detail.page !== this.closest('product-page')) return;
        const id = e.detail?.variant?.featured_media?.id;
        if (!id) return;
        const slide = this.slides.find((s) => String(s.dataset.mediaId) === String(id));
        if (slide) this.go(parseInt(slide.dataset.index, 10));
      });
      // al caricamento parte dalla foto della variante selezionata (non dalla prima del prodotto)
      const initial = this.dataset.initialMedia;
      if (initial) {
        const slide = this.slides.find((sl) => String(sl.dataset.mediaId) === String(initial));
        if (slide) {
          this.index = parseInt(slide.dataset.index, 10);
          const prev = this.track.style.scrollBehavior;
          this.track.style.scrollBehavior = 'auto';
          this.track.scrollLeft = this.track.clientWidth * this.index;
          this.track.style.scrollBehavior = prev;
        }
      }
      this.setupZoom();
      this.render();
    }

    /* ---- zoom: finestra a tutto schermo con le sole foto; clic sulla foto = doppio, si scorre per spostarsi ---- */
    setupZoom() {
      this.zoom = this.querySelector('[data-zoom]');
      if (!this.zoom) return;
      this.zoomStage = this.zoom.querySelector('[data-zoom-stage]');
      // l'immagine nasce qui: ha la sorgente solo a finestra aperta
      this.zoomImg = document.createElement('img');
      this.zoomImg.className = 'zoom__img';
      this.zoomImg.draggable = false;
      this.zoomImg.alt = ''; // l'alt vero arriva con la foto, all'apertura
      this.zoomStage.append(this.zoomImg);
      this.zoomCounter = this.zoom.querySelector('[data-zoom-counter]');
      this.zoomPrev = this.zoom.querySelector('[data-zoom-prev]');
      this.zoomNext = this.zoom.querySelector('[data-zoom-next]');
      this.zoomButtons = [...this.querySelectorAll('[data-gallery-zoom]')];
      this.zoomButtons.forEach((b, i) => b.addEventListener('click', () => this.openZoom(i, b)));
      this.zoomPrev.addEventListener('click', () => this.showZoom(this.zoomAt - 1));
      this.zoomNext.addEventListener('click', () => this.showZoom(this.zoomAt + 1));
      this.zoom.querySelector('[data-zoom-close]').addEventListener('click', () => this.zoom.close());
      this.zoom.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.showZoom(this.zoomAt - 1);
        if (e.key === 'ArrowRight') this.showZoom(this.zoomAt + 1);
      });
      this.zoomStage.addEventListener('click', (e) => {
        if (e.target !== this.zoomImg) return;
        const r = this.zoomImg.getBoundingClientRect();
        const fx = (e.clientX - r.left) / r.width;
        const fy = (e.clientY - r.top) / r.height;
        const on = this.zoom.classList.toggle('is-zoomed');
        // ingrandendo, il punto cliccato resta sotto il dito/puntatore
        if (on) {
          const st = this.zoomStage;
          st.scrollLeft = fx * st.scrollWidth - (e.clientX - st.getBoundingClientRect().left);
          st.scrollTop = fy * st.scrollHeight - (e.clientY - st.getBoundingClientRect().top);
        }
      });
      this.zoom.addEventListener('close', () => {
        this.releaseZoomFocus?.();
        this.zoom.classList.remove('is-zoomed');
        // la galleria resta sulla foto vista per ultima
        const slide = this.zoomButtons[this.zoomAt]?.closest('.gallery__slide');
        if (slide) this.go(parseInt(slide.dataset.index, 10));
        this.zoomButtons[this.zoomAt]?.focus();
      });
    }
    openZoom(i) {
      this.showZoom(i);
      this.zoom.showModal();
      this.releaseZoomFocus = window.themeTrapFocus(this.zoom, this.zoom.querySelector('[data-zoom-close]'));
    }
    showZoom(i) {
      const n = this.zoomButtons.length;
      if (!n) return;
      this.zoomAt = Math.max(0, Math.min(n - 1, i));
      const b = this.zoomButtons[this.zoomAt];
      this.zoom.classList.remove('is-zoomed');
      this.zoomImg.src = b.dataset.zoomSrc;
      this.zoomImg.alt = b.dataset.zoomAlt || '';
      this.zoomStage.scrollTo(0, 0);
      this.zoomPrev.disabled = this.zoomAt === 0;
      this.zoomNext.disabled = this.zoomAt === n - 1;
      this.zoomPrev.hidden = this.zoomNext.hidden = n < 2;
      this.zoomCounter.textContent = `${this.zoomAt + 1} / ${n}`;
    }
    go(i) {
      if (!this.slides.length) return;
      const target = Math.max(0, Math.min(this.slides.length - 1, i));
      // scorrere fino a una foto lontana (cambio colore, miniatura in fondo) significa far sfilare
      // tutte quelle in mezzo: oltre la foto adiacente si passa con una dissolvenza e un salto secco
      const lontana = Math.abs(target - this.index) > 1;
      this.index = target;
      const left = this.track.clientWidth * this.index;
      if (lontana) this.saltaA(left);
      else this.track.scrollTo({ left, behavior: 'smooth' });
      this.render();
    }

    saltaA(left) {
      const t = this.track;
      const salta = () => {
        const prima = t.style.scrollBehavior;
        t.style.scrollBehavior = 'auto';
        t.scrollLeft = left;
        t.style.scrollBehavior = prima;
        requestAnimationFrame(() => t.classList.remove('is-fading'));
      };
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return salta();
      t.classList.add('is-fading');
      clearTimeout(this.fadeTimer);
      this.fadeTimer = setTimeout(salta, 130);
    }
    sync() {
      const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
      if (i !== this.index) {
        this.index = i;
        this.render();
      }
    }
    render() {
      this.slides.forEach((s, i) => {
        if (i !== this.index) s.querySelector('video')?.pause?.();
      });
      this.thumbs.forEach((t, i) => {
        t.classList.toggle('is-active', i === this.index);
        // scorre solo la striscia delle miniature (mai la pagina: scrollIntoView farebbe scorrere il documento)
        if (i === this.index && t.parentElement) {
          const strip = t.parentElement;
          const left = t.offsetLeft - (strip.clientWidth - t.offsetWidth) / 2;
          if (Math.abs(strip.scrollLeft - left) > 2) strip.scrollTo({ left, behavior: this.rendered ? 'smooth' : 'auto' });
        }
      });
      this.rendered = true;
      if (this.prev) this.prev.disabled = this.index === 0;
      if (this.next) this.next.disabled = this.index >= this.slides.length - 1;
      if (this.counter) {
        const total = parseInt(this.dataset.count, 10) || this.slides.length;
        // anche qui il testo si ricostruisce dal modello, invece di cercare le cifre dentro la frase:
        // una traduzione con altri numeri o un ordine diverso manderebbe fuori strada la sostituzione
        const modello = this.counter.dataset.counterTemplate;
        this.counter.textContent = modello
          ? modello.replace('[i]', String(this.index + 1)).replace('[n]', String(total))
          : `${this.index + 1} / ${total}`;
      }
      const isVideo = /video/.test(this.slides[this.index]?.dataset.mediaType || '');
      this.querySelectorAll('[data-gallery-filter]').forEach((b) => b.classList.toggle('is-active', (b.dataset.galleryFilter === 'video') === isVideo));
    }
  }
  customElements.define('product-gallery', ProductGallery);

  /* ---- visti di recente: la scheda aperta va in cima alla lista (letta dalla sezione recently-viewed) ---- */
  const visto = document.querySelector('[data-product-handle]')?.dataset.productHandle;
  if (visto) {
    try {
      const lista = JSON.parse(localStorage.getItem('torque:recently-viewed') || '[]').filter((h) => h !== visto);
      localStorage.setItem('torque:recently-viewed', JSON.stringify([visto, ...lista].slice(0, 12)));
    } catch (e) {
      /* storage disattivato: la sezione resta nascosta */
    }
  }

  /* ---- quantità ---- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelector('[data-qty-minus]')?.addEventListener('click', () => this.step(-1));
      this.querySelector('[data-qty-plus]')?.addEventListener('click', () => this.step(1));
    }
    step(n) {
      const min = parseInt(this.input.min, 10) || 1;
      this.input.value = Math.max(min, (parseInt(this.input.value, 10) || min) + n);
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---- varianti ---- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      try {
        this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      } catch (e) {
        this.variants = [];
      }
      this.page = this.closest('product-page');
      this.addEventListener('change', () => this.update());
    }
    selected() {
      const opts = [];
      this.querySelectorAll('input[type="radio"]:checked').forEach((r) => (opts[parseInt(r.dataset.optionIndex, 10)] = r.value));
      return opts;
    }
    update() {
      const opts = this.selected();
      const variant = this.variants.find((v) => v.options.every((o, i) => o === opts[i]));
      this.querySelectorAll('fieldset').forEach((f, i) => {
        const el = f.querySelector('[data-option-value]');
        if (el) el.textContent = opts[i] || '';
      });
      const page = this.page || document;
      const idInput = page.querySelector('[data-variant-id]');
      const add = page.querySelector('[data-add-to-cart]');
      const label = page.querySelector('[data-add-label]');
      const strings = window.theme?.strings || {};
      if (!variant) {
        if (add) add.disabled = true;
        if (label) label.textContent = strings.unavailable || 'Unavailable';
        return;
      }
      if (idInput) idInput.value = variant.id;
      if (add) add.disabled = !variant.available;
      if (label) label.textContent = variant.available ? strings.addToCart || 'Add to cart' : strings.soldOut || 'Sold out';
      const current = page.querySelector('[data-price-current]');
      const compare = page.querySelector('[data-price-compare]');
      const save = page.querySelector('[data-price-save]');
      if (current) current.textContent = window.themeMoney(variant.price);
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (compare) {
        compare.hidden = !onSale;
        if (onSale) compare.textContent = window.themeMoney(variant.compare_at_price);
      }
      if (save) {
        save.hidden = !onSale;
        // il testo si ricostruisce dal modello ("Risparmi [importo]"): riscrivere sopra quello vecchio
        // lasciava il simbolo di valuta della volta prima, che si accumulava a ogni cambio di colore
        if (onSale) {
          const importo = window.themeMoney(variant.compare_at_price - variant.price);
          const modello = save.dataset.saveTemplate;
          save.textContent = modello ? modello.replace('[importo]', importo) : importo;
        }
      }
      page.querySelector('[data-price]')?.classList.toggle('price--sale', !!onSale);
      if (this.page?.classList.contains('product-main')) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
      }
      // page: la scheda (o l'anteprima) da cui arriva il cambio, perché ogni componente ascolti solo la sua
      document.dispatchEvent(new CustomEvent('theme:variant:change', { detail: { variant, page: this.page } }));
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ---- accessori: select variante → id nel form ---- */
  document.addEventListener('change', (e) => {
    if (!e.target.matches('[data-upsell-variant]')) return;
    const item = e.target.closest('.product-main__upsell-item');
    const id = item?.querySelector('[data-upsell-id]');
    if (id) id.value = e.target.value;
  });

  /* ---- feedback "aggiunto" sul bottone ---- */
  document.addEventListener('theme:cart:add', () => {
    const add = document.querySelector('[data-add-to-cart]');
    const label = add?.querySelector('[data-add-label]');
    if (!label) return;
    const original = label.textContent;
    label.textContent = window.theme?.strings?.added || 'Added';
    setTimeout(() => (label.textContent = original), 1500);
  });

  /* ---- avviso "torna disponibile" (form nativo 'customer', tag per variante) ----
     L'invio è quello normale del form: Shopify risponde con la sua verifica antibot e poi
     reindirizza alla scheda con ?customer_posted=true, dove Liquid mostra il messaggio.
     Un invio via fetch riceve 403 "Verifying your connection" e non arriva mai (provato il 01/10). */
  class BackInStock extends HTMLElement {
    connectedCallback() {
      this.tags = this.querySelector('[data-back-in-stock-tags]');
      this.ok = this.querySelector('[data-back-in-stock-ok]');
      this.error = this.querySelector('[data-back-in-stock-error]');
      this.submit = this.querySelector('[data-back-in-stock-submit]');
      document.addEventListener('theme:variant:change', (e) => {
        if (e.detail?.page && e.detail.page !== this.closest('product-page')) return;
        this.onVariant(e.detail?.variant);
      });
      this.querySelector('[data-back-in-stock-form]')?.addEventListener('submit', () => this.submit?.classList.add('is-loading'));
      if (this.ok && !this.ok.hidden) this.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
    onVariant(v) {
      if (!v) return;
      this.hidden = !!v.available;
      if (this.tags) {
        const tags = ['avvisami', `avvisami:${v.id}`];
        if (v.sku) tags.push(`avvisami-sku:${v.sku}`);
        this.tags.value = tags.join(', ');
      }
      if (this.ok) this.ok.hidden = true;
      if (this.error) this.error.hidden = true;
    }
  }
  customElements.define('back-in-stock', BackInStock);

  /* ---- ultimi pezzi: la quantità della variante scelta, sotto la soglia del blocco ---- */
  document.querySelectorAll('[data-stock-counter]').forEach((box) => {
    const page = box.closest('product-page');
    let stock = {};
    try {
      stock = JSON.parse(box.parentElement.querySelector('[data-stock]')?.textContent || '{}');
    } catch (e) {
      stock = {};
    }
    const soglia = parseInt(box.dataset.threshold, 10) || 5;
    const testo = box.querySelector('[data-stock-text]');
    document.addEventListener('theme:variant:change', (e) => {
      if (e.detail?.page && e.detail.page !== page) return;
      const q = stock[String(e.detail?.variant?.id)];
      const basso = typeof q === 'number' && q > 0 && q <= soglia;
      box.hidden = !basso;
      if (basso) testo.textContent = q === 1 ? box.dataset.one : box.dataset.other.replace('[n]', String(q));
    });
  });

  /* ---- ritiro in negozio: disponibilità della variante scelta (sezione pickup-availability via Section Rendering) ---- */
  class PickupAvailability extends HTMLElement {
    connectedCallback() {
      this.page = this.closest('product-page');
      this.load(this.dataset.variantId);
      document.addEventListener('theme:variant:change', (e) => {
        if (e.detail?.page && e.detail.page !== this.page) return;
        if (e.detail?.variant?.id) this.load(e.detail.variant.id);
      });
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-pickup-open]')) {
          const d = this.querySelector('dialog');
          d?.showModal();
          const release = window.themeTrapFocus?.(d, d.querySelector('[data-pickup-close]'));
          d?.addEventListener('close', () => release?.(), { once: true });
        } else if (e.target.closest('[data-pickup-close]')) {
          e.target.closest('dialog').close();
        } else if (e.target.matches('dialog')) {
          e.target.close();
        }
      });
    }
    async load(id) {
      if (!id) return;
      try {
        const url = new URL(this.dataset.url, window.location.origin);
        url.searchParams.set('variant', id);
        url.searchParams.set('section_id', 'pickup-availability');
        const res = await fetch(url.toString());
        if (!res.ok) throw new Error(String(res.status));
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const content = doc.querySelector('[data-pickup-content]');
        // nessun punto di ritiro per questa variante: il riquadro sparisce
        this.replaceChildren(...(content ? [content] : []));
      } catch (e) {
        this.replaceChildren();
      }
    }
  }
  customElements.define('pickup-availability', PickupAvailability);

  /* ---- barra sticky ---- */
  class ProductStickyNav extends HTMLElement {
    connectedCallback() {
      this.target = document.querySelector('#MainProductInfo') || document.querySelector('.product-main');
      // una voce che punta a una sezione assente (disattivata dal theme editor, o vuota perché il
      // prodotto non ha quei dati) viene tolta dalla barra invece di restare come link morto
      this.tabs = [...this.querySelectorAll('[data-anchor]')].filter((t) => {
        if (document.getElementById(t.dataset.anchor)) return true;
        t.remove();
        return false;
      });
      this.querySelector('.sticky-nav__tabs')?.toggleAttribute('hidden', this.tabs.length === 0);
      this.idInput = this.querySelector('[data-variant-id]');
      this.variantLabel = this.querySelector('[data-sticky-variant]');
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
      document.addEventListener('theme:variant:change', (e) => {
        // la barra compra il prodotto della scheda: un'anteprima rapida aperta sopra non deve cambiarle variante
        if (e.detail?.page && !e.detail.page.classList.contains('product-main')) return;
        const v = e.detail?.variant;
        if (!v) return;
        if (this.idInput) this.idInput.value = v.id;
        if (this.variantLabel) this.variantLabel.textContent = v.title === 'Default Title' ? '' : v.title;
        const add = this.querySelector('[data-sticky-add]');
        if (add) add.disabled = !v.available;
      });
      this.tabs.forEach((t) =>
        t.addEventListener('click', (e) => {
          const el = document.getElementById(t.dataset.anchor);
          if (!el) return;
          e.preventDefault();
          window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - this.offsetHeight - 8, behavior: 'smooth' });
        })
      );
    }
    onScroll() {
      if (!this.target) return;
      const rect = this.target.getBoundingClientRect();
      this.classList.toggle('is-visible', rect.bottom < 0);
      let active = null;
      this.tabs.forEach((t) => {
        const el = document.getElementById(t.dataset.anchor);
        if (el && el.getBoundingClientRect().top <= this.offsetHeight + 40) active = t;
      });
      this.tabs.forEach((t) => t.classList.toggle('is-active', t === active));
    }
  }
  customElements.define('product-sticky-nav', ProductStickyNav);

  /* ---- prodotti consigliati (Section Rendering API) ---- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      const load = async () => {
        try {
          const res = await fetch(this.dataset.url);
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('product-recommendations');
          if (fresh && fresh.innerHTML.trim()) {
            this.innerHTML = fresh.innerHTML;
            window.themeObserveReveal?.(this);
          } else {
            this.closest('.shopify-section')?.remove();
          }
        } catch (e) {
          console.error(e);
        }
      };
      new IntersectionObserver((entries, obs) => {
        if (entries.some((en) => en.isIntersecting)) {
          obs.disconnect();
          load();
        }
      }, { rootMargin: '400px 0px' }).observe(this);
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);
})();
